/**
 * <site-header>
 *
 * Drives two things in sections/header.liquid: the mobile drawer, and the desktop
 * mega menus.
 *
 * ---------------------------------------------------------------------------
 * MEGA MENUS
 *
 * Opening one does three things at once, and they are one operation rather than
 * three because they must never be seen out of step: the panel fades in, the
 * header bar re-themes to the panel's colour scheme, and the logo swaps to that
 * menu's own mark if it has one.
 *
 * THE `data-scheme-override` ATTRIBUTE IS LOAD-BEARING. The section's style block
 * paints the header from the colour scheme of whatever section sits below it,
 * using :has() rules that land around (0,5,0). A `.color-…` class added here is
 * (0,1,0) and would silently lose that contest. Setting the attribute is what
 * switches those rules off so this one can win. Remove it and the mega menu
 * appears to do nothing at all.
 *
 * Hover is tracked with mouseenter on each nav ITEM and mouseleave on the whole
 * HEADER, not both on the item. The panel is absolutely positioned below the bar,
 * so travelling from a link down into it crosses header padding that belongs to
 * no list item — leaving on the item would flicker the panel shut halfway. The
 * same split applies to focusin/focusout for keyboard users.
 *
 * Panels are hidden with `inert` plus zero opacity, never display:none. inert
 * removes them from the tab order and the accessibility tree, which a merely
 * transparent element does not, and unlike a display change it lets the fade run.
 *
 * Desktop only — the mobile drawer owns the same links below 768px.
 *
 * DELIBERATELY SMALL, because the markup is a real <dialog> opened with
 * showModal(). Three things that a hand-rolled drawer has to build are supplied
 * by the platform here and are NOT reimplemented below:
 *
 *   - the focus trap, and returning focus to the opener on close
 *   - Esc to dismiss
 *   - inertness of the page behind the drawer
 *
 * A fourth, page scroll locking, comes from the `scroll-lock` attribute in the
 * markup: assets/critical.css already carries
 * `html:has(dialog[scroll-lock][open]) { overflow: hidden }`. That is an existing
 * theme convention and needs no JavaScript either.
 *
 * WHY THE PANEL SLIDES AND THE DIALOG DOES NOT. A dialog goes from display:none
 * to display:block, and a transform transition cannot run across a display
 * change without @starting-style and allow-discrete. So the dialog is opened
 * instantly and a child panel is transitioned instead, toggled by a data
 * attribute on the next frame — the element has to have been laid out in its
 * start position before the end position is applied, or the browser has nothing
 * to interpolate from and it simply snaps.
 *
 * No prefers-reduced-motion check here. The only movement is a CSS transform
 * already behind Tailwind's motion-safe: variant, so a visitor who has asked for
 * reduced motion gets an instant open from the stylesheet. A matchMedia guard
 * would be dead code pretending to do something — the same conclusion
 * hover-list.js reached and documented.
 */
if (!customElements.get('site-header')) {
  customElements.define(
    'site-header',
    class SiteHeader extends HTMLElement {
      /* Must stay in step with the design system's md breakpoint — 48rem in
         assets/css-tailwind.css, and the md: utilities on the drawer markup. */
      static DESKTOP_BREAKPOINT = 768;

      /* Long enough to cover the panel's motion-safe transition (300ms) with a
         little slack. Only a fallback: transitionend normally fires first. */
      static CLOSE_FALLBACK_MS = 400;

      /* Minimum scroll distance before the sticky header changes direction.
         Small enough to feel responsive, large enough that trackpad jitter and
         iOS rubber-banding do not flap it open and shut. */
      static SCROLL_THRESHOLD = 6;

      connectedCallback() {
        this.dialog = this.querySelector('[data-drawer]');
        this.panel = this.querySelector('[data-drawer-panel]');
        this.openButton = this.querySelector('[data-drawer-open]');
        this.closeButton = this.querySelector('[data-drawer-close]');
        this.toggles = Array.from(this.querySelectorAll('[data-accordion-toggle]'));

        /* Mega menus. The header element is the thing that gets re-themed, and it
           is this component's PARENT rather than a descendant — the custom
           element sits inside <header> so that `.shopify-section > header` stays
           true, which two shipped CSS rules depend on. */
        this.header = this.closest('[data-header]');
        this.navItems = Array.from(this.querySelectorAll('[data-nav-item]'));
        this.megaPanels = Array.from(this.querySelectorAll('[data-mega-panel]'));
        /* Logos are keyed by COLOUR SCHEME CLASS — the same value applyHeaderScheme
           takes — because the scheme painting the bar is the only thing that ever
           decided which mark was correct. There used to be three key spaces here
           (a mega block id, the string 'search', and the default); they collapsed
           into one when the logos moved to a sitewide per-scheme map.

           Note the drawer's logo is inside this component too, and deliberately
           carries NO marker attributes — see snippets/header-logo.liquid. If it
           ever gained the default marker, querySelector below would be a coin flip
           between it and the real header logo. */
        this.schemeLogos = Array.from(this.querySelectorAll('[data-header-logo-scheme]'));
        this.defaultLogo = this.querySelector('[data-header-logo-default]');
        /* Every logo in one list, so showLogo only ever has to mark one active
           and clear the rest — no per-kind bookkeeping. */
        this.allLogos = [this.defaultLogo, ...this.schemeLogos].filter(Boolean);

        this.searchPanel = this.querySelector('[data-search-panel]');
        this.searchInput = this.querySelector('[data-search-input]');
        this.searchToggles = Array.from(this.querySelectorAll('[data-search-toggle]'));
        this.searchClose = this.querySelector('[data-search-close]');
        this.searchOpen = false;

        /* Whatever colour class the header rendered with, so it can be put back
           on close. Read rather than hardcoded: it comes from a merchant setting
           and, on templates where the "follow the section below" rules apply, is
           only the fallback. */
        this.defaultSchemeClass = this.header
          ? Array.from(this.header.classList).find((name) => name.startsWith('color-'))
          : null;

        this.openMegaId = null;
        this.closeTimer = null;

        /* Sticky state. `panelScheme` and `pinned` are the two inputs to
           resolveScheme(); see the note there for why they are resolved together
           rather than each painting the bar directly. */
        this.panelScheme = null;
        this.pinned = false;
        this.revealed = false;
        this.stickyScheme = this.header?.dataset.stickyScheme || null;
        this.lastScrollY = Math.max(0, window.scrollY);
        this.scrollTicking = false;
        this.headerHeight = 0;

        this.handleOpen = this.handleOpen.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
        this.handleDialogClose = this.handleDialogClose.bind(this);
        this.handleBackdropClick = this.handleBackdropClick.bind(this);
        this.handleAccordion = this.handleAccordion.bind(this);
        this.handleBreakpoint = this.handleBreakpoint.bind(this);
        this.handleNavEnter = this.handleNavEnter.bind(this);
        this.handleHeaderLeave = this.handleHeaderLeave.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleSearchToggle = this.handleSearchToggle.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        /* A named bound method, not an inline arrow — an arrow cannot be handed
           back to removeEventListener, so it would survive disconnect. */
        this.handleSearchClose = this.handleSearchClose.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleHeaderFocusIn = this.handleHeaderFocusIn.bind(this);

        this.openButton?.addEventListener('click', this.handleOpen);
        this.closeButton?.addEventListener('click', this.handleClose);
        this.dialog?.addEventListener('cancel', this.handleCancel);
        this.dialog?.addEventListener('close', this.handleDialogClose);
        this.dialog?.addEventListener('click', this.handleBackdropClick);
        this.toggles.forEach((toggle) => toggle.addEventListener('click', this.handleAccordion));

        /* mouseenter and focusin are bound per item: mouseenter does not bubble,
           and delegating focusin from the root would fire for the logo and the
           cart link too. */
        this.navItems.forEach((item) => {
          item.addEventListener('mouseenter', this.handleNavEnter);
          item.addEventListener('focusin', this.handleNavEnter);
        });

        if (this.megaPanels.length && this.header) {
          this.header.addEventListener('mouseleave', this.handleHeaderLeave);
          this.header.addEventListener('focusout', this.handleFocusOut);
        }

        if (this.searchPanel) {
          this.searchToggles.forEach((t) => t.addEventListener('click', this.handleSearchToggle));
          this.searchClose?.addEventListener('click', this.handleSearchClose);
        }

        /* Esc serves both the mega menus and the search, so it is bound whenever
           either exists rather than only alongside the mega menus. */
        this.header?.addEventListener('keydown', this.handleKeydown);

        /* passive: this handler never calls preventDefault, and saying so lets the
           browser scroll without waiting on it. */
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleResize);
        this.header?.addEventListener('focusin', this.handleHeaderFocusIn);

        this.measureHeader();
        this.updateStickyState();

        this.desktop = window.matchMedia(`(min-width: ${SiteHeader.DESKTOP_BREAKPOINT}px)`);
        this.desktop.addEventListener('change', this.handleBreakpoint);
      }

      disconnectedCallback() {
        this.openButton?.removeEventListener('click', this.handleOpen);
        this.closeButton?.removeEventListener('click', this.handleClose);
        this.dialog?.removeEventListener('cancel', this.handleCancel);
        this.dialog?.removeEventListener('close', this.handleDialogClose);
        this.dialog?.removeEventListener('click', this.handleBackdropClick);
        this.toggles?.forEach((toggle) => toggle.removeEventListener('click', this.handleAccordion));

        this.navItems?.forEach((item) => {
          item.removeEventListener('mouseenter', this.handleNavEnter);
          item.removeEventListener('focusin', this.handleNavEnter);
        });

        this.header?.removeEventListener('mouseleave', this.handleHeaderLeave);
        this.header?.removeEventListener('focusout', this.handleFocusOut);
        this.header?.removeEventListener('keydown', this.handleKeydown);

        this.searchToggles?.forEach((t) => t.removeEventListener('click', this.handleSearchToggle));
        this.searchClose?.removeEventListener('click', this.handleSearchClose);
        document.removeEventListener('click', this.handleDocumentClick);

        this.desktop?.removeEventListener('change', this.handleBreakpoint);
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
        this.header?.removeEventListener('focusin', this.handleHeaderFocusIn);

        if (this.closeTimer) {
          clearTimeout(this.closeTimer);
        }
      }

      /* ----------------------------------------------------------------- */
      /* Mega menus                                                        */
      /* ----------------------------------------------------------------- */

      handleNavEnter(event) {
        if (!this.desktop.matches) {
          return;
        }

        /* Every top-level item reports, including those without a panel —
           hovering a plain link has to dismiss whatever is open. */
        const id = event.currentTarget.dataset.megaTrigger;

        if (id) {
          this.openMega(id);
        } else {
          this.closeMega();
        }
      }

      handleHeaderLeave() {
        this.closeMega();
      }

      /** Closes once focus has genuinely left the header, rather than moved within it. */
      handleFocusOut(event) {
        if (!this.header.contains(event.relatedTarget)) {
          this.closeMega();
          this.closeSearch();
        }
      }

      handleKeydown(event) {
        if (event.key !== 'Escape') {
          return;
        }

        if (this.searchOpen) {
          this.closeSearch({ restoreFocus: true });
          return;
        }

        if (!this.openMegaId) {
          return;
        }

        /* Focus is somewhere inside the panel that is about to become inert, so
           it has to be moved out first — an inert element cannot hold focus, and
           the browser would drop it to <body> and lose the visitor's place. */
        const trigger = this.querySelector(`[data-mega-trigger="${CSS.escape(this.openMegaId)}"] a`);

        this.closeMega();
        trigger?.focus();
      }

      /* ----------------------------------------------------------------- */
      /* Search                                                            */
      /* ----------------------------------------------------------------- */

      handleSearchClose() {
        this.closeSearch({ restoreFocus: true });
      }

      handleSearchToggle(event) {
        /* The trigger is a real link to /search so it works without scripting.
           Intercepting is what turns it into a dropdown. */
        event.preventDefault();

        if (this.searchOpen) {
          this.closeSearch({ restoreFocus: true });
        } else {
          this.openSearch(event.currentTarget);
        }
      }

      openSearch(trigger) {
        /* Only one panel below the bar at a time — an open mega menu would sit
           behind the search and both would be re-theming the same header. */
        this.closeMega();

        this.searchOpen = true;
        this.searchTrigger = trigger ?? null;

        this.searchPanel.inert = false;
        this.searchPanel.dataset.open = '';
        this.searchToggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));

        this.setPanelScheme(this.searchPanel.dataset.searchScheme);

        /* Focus moves to the field immediately. Without it a keyboard visitor
           would have to tab past the rest of the header to reach the input they
           just asked for. */
        this.searchInput?.focus();

        /* Bound only while open, and on the next frame so the click that opened
           the panel does not immediately close it again. */
        requestAnimationFrame(() => {
          document.addEventListener('click', this.handleDocumentClick);
        });
      }

      closeSearch({ restoreFocus = false } = {}) {
        if (!this.searchOpen) {
          return;
        }

        this.searchOpen = false;

        /* Focus is moved out BEFORE the panel goes inert. An inert element
           cannot hold focus, and the browser would otherwise drop it to <body>. */
        if (restoreFocus && this.searchTrigger) {
          this.searchTrigger.focus();
        } else if (this.searchPanel.contains(document.activeElement)) {
          document.activeElement.blur();
        }

        this.searchPanel.inert = true;
        delete this.searchPanel.dataset.open;
        this.searchToggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));

        this.setPanelScheme(null);

        document.removeEventListener('click', this.handleDocumentClick);
      }

      handleDocumentClick(event) {
        if (!this.header.contains(event.target)) {
          this.closeSearch();
        }
      }

      openMega(id) {
        if (this.openMegaId === id) {
          return;
        }

        /* Both panels drop from the same edge and both re-theme the same bar, so
           they cannot be open together. */
        this.closeSearch();

        this.openMegaId = id;

        this.megaPanels.forEach((panel) => {
          const isActive = panel.dataset.megaPanel === id;

          panel.inert = !isActive;

          if (isActive) {
            panel.dataset.open = '';
          } else {
            delete panel.dataset.open;
          }
        });

        this.navItems.forEach((item) => {
          const trigger = item.querySelector('a[aria-expanded]');
          if (trigger) {
            trigger.setAttribute('aria-expanded', String(item.dataset.megaTrigger === id));
          }
        });

        const active = this.megaPanels.find((panel) => panel.dataset.megaPanel === id);
        this.setPanelScheme(active?.dataset.megaScheme);
      }

      closeMega() {
        if (!this.openMegaId) {
          return;
        }

        this.openMegaId = null;

        this.megaPanels.forEach((panel) => {
          panel.inert = true;
          delete panel.dataset.open;
        });

        this.navItems.forEach((item) => {
          item.querySelector('a[aria-expanded]')?.setAttribute('aria-expanded', 'false');
        });

        this.setPanelScheme(null);
      }

      /* ----------------------------------------------------------------- */
      /* Sticky header                                                     */
      /* ----------------------------------------------------------------- */

      /**
       * The header's own height, which is the scroll distance at which it has
       * fully left the screen. Measured rather than computed from the tokens: it
       * is the sum of two custom properties on two elements, and reading the
       * rendered box cannot drift out of step with them.
       */
      measureHeader() {
        this.headerHeight = this.header?.offsetHeight ?? 0;
      }

      handleResize() {
        this.measureHeader();
        this.updateStickyState();
      }

      /**
       * rAF-throttled, because scroll fires far more often than the screen
       * repaints and every run of updateStickyState touches classes.
       */
      handleScroll() {
        if (this.scrollTicking) {
          return;
        }

        this.scrollTicking = true;
        requestAnimationFrame(() => {
          this.scrollTicking = false;
          this.updateStickyState();
        });
      }

      /**
       * Decides pin/reveal from scroll position and direction.
       *
       * 🚨 THE PIN AND UNPIN THRESHOLDS ARE DIFFERENT NUMBERS, and that asymmetry
       * is the whole trick. Using one threshold for both looks obviously right and
       * is obviously wrong once you work out where the element actually sits:
       *
       *   - A PINNED header is fixed at screen y = 0.
       *   - An UNPINNED header is in flow at document y = 0, which is screen
       *     y = -scrollY.
       *
       * Those two positions coincide at exactly one scroll position: zero. Unpin
       * anywhere else and the bar teleports by however far you have scrolled — so
       * unpinning at, say, the header's own height would make a revealed header
       * vanish upward mid-scroll, then reappear as you carried on to the top.
       *
       * So:
       *   PIN only past the header's height, by which point it has fully left the
       *   screen and the switch to fixed cannot be seen. Below that it stays in
       *   flow and scrolls away naturally, which is what was asked for.
       *
       *   UNPIN only at the very top, where the handoff is pixel-for-pixel
       *   invisible. Between the two the header stays pinned, which is correct:
       *   scrolling up through that band it is already sitting where it belongs,
       *   and the reserved space behind it is empty rather than showing a second
       *   copy.
       */
      updateStickyState() {
        if (!this.header) {
          return;
        }

        const y = Math.max(0, window.scrollY);

        /* Back at the top: hand back to the natural, in-flow header, which
           resolves to transparent over an overlay hero or to the colour of the
           section below. Sub-pixel tolerance because scrollY is fractional on
           zoomed and high-DPI displays. */
        if (y < 1) {
          this.unpin();
          this.lastScrollY = y;
          return;
        }

        const delta = y - this.lastScrollY;

        /* Below the threshold nothing happens AND lastScrollY is left alone, so
           small movements accumulate instead of being swallowed. A plain
           per-event direction check flickers on trackpads and rubber-banding. */
        if (Math.abs(delta) < SiteHeader.SCROLL_THRESHOLD) {
          return;
        }

        /* Only take over once the header has genuinely left the screen. Inside
           that band an unpinned header is still scrolling away under its own
           steam and must be left alone; a pinned one stays pinned until y hits 0. */
        if (y > this.headerHeight || this.pinned) {
          this.pin(delta < 0);
        }

        this.lastScrollY = y;
      }

      /**
       * @param {boolean} reveal - whether the header should be showing once pinned.
       */
      pin(reveal) {
        if (this.pinned) {
          this.setRevealed(reveal);
          return;
        }

        this.pinned = true;
        this.header.dataset.pinned = '';
        this.resolveScheme();

        /* Two frames before the transform transition is allowed, exactly as the
           drawer does. The element has to be laid out in its parked position
           first — otherwise the browser animates the jump from "in flow" to
           "translated off-screen" and a fully visible bar slides up the screen
           every time you scroll past the header.

           THE FIRST REVEAL IS DEFERRED INTO THIS SAME CHAIN, and that matters.
           Revealing in the caller's tick would land two frames BEFORE the
           transition is enabled, so that first appearance would snap instead of
           easing. Normally invisible — the usual first pin happens while
           scrolling down, where reveal is false — but a page restored at a
           scrolled position and then scrolled UP pins and reveals in one go, and
           that is precisely the case a visitor sees first. */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!this.pinned) {
              return;
            }

            this.header.dataset.pinReady = '';
            this.setRevealed(reveal);
          });
        });
      }

      unpin() {
        if (!this.pinned) {
          return;
        }

        this.pinned = false;
        this.revealed = false;
        delete this.header.dataset.pinned;
        delete this.header.dataset.revealed;
        delete this.header.dataset.pinReady;
        this.resolveScheme();
      }

      setRevealed(next) {
        if (this.revealed === next) {
          return;
        }

        this.revealed = next;

        if (next) {
          this.header.dataset.revealed = '';
        } else {
          delete this.header.dataset.revealed;
        }
      }

      /**
       * A pinned-but-hidden header is translated off-screen, NOT removed from the
       * tab order — so tabbing into it would move focus somewhere invisible.
       * Revealing on focus is what keeps it keyboard-operable.
       */
      handleHeaderFocusIn() {
        if (this.pinned) {
          this.setRevealed(true);
        }
      }

      /**
       * Records which scheme an OPEN PANEL wants, then re-resolves. Null clears it.
       */
      setPanelScheme(schemeClass) {
        this.panelScheme = schemeClass || null;
        this.resolveScheme();
      }

      /**
       * THE SINGLE PLACE THE HEADER'S COLOUR IS DECIDED.
       *
       * Two independent things now want to repaint the bar — an open mega menu or
       * search panel, and the sticky header sliding back down — and they can be
       * true at the same time. Resolving both here rather than letting each call
       * applyHeaderScheme directly is what stops them fighting: whichever ran last
       * would otherwise win, so scrolling with a panel open could silently swap
       * the panel's colour for the sticky one.
       *
       * An open panel outranks the sticky state, because the panel is the thing
       * the visitor is looking at.
       *
       * The logo follows the same resolved value, so the mark and the colour can
       * never disagree — that was previously two calls at every call site, which
       * is exactly the kind of pairing that drifts.
       */
      resolveScheme() {
        const scheme = this.panelScheme ?? (this.pinned ? this.stickyScheme : null);

        this.applyHeaderScheme(scheme);
        this.showLogo(scheme);

        /* THE BOTTOM RULE IS ONE DERIVED FLAG, not two competing CSS conditions.
         *
         * It draws the pinned bar's bottom edge — so it wants to be there when
         * pinned, and NOT when a mega menu or the search is open, because those
         * panels butt directly against that edge and the bar and panel read as
         * one block. A line between them looks like a mistake.
         *
         * Expressed as a single positive attribute rather than as "show when
         * pinned, hide when a panel is open". Those would be two Tailwind group
         * variants at identical specificity, and the winner would be whichever
         * Tailwind happened to emit last — the coin flip this project has been
         * caught by more than once. Deriving it here means the CSS has exactly
         * one condition and cannot tie.
         */
        this.header?.toggleAttribute('data-show-rule', this.pinned && !this.panelScheme);
      }

      /**
       * Swaps the header's colour class, and sets the override attribute that
       * stops the section's :has() rules out-specifying it. Passing null restores
       * whatever the header rendered with.
       *
       * Call resolveScheme(), not this — this does not know about precedence.
       */
      applyHeaderScheme(schemeClass) {
        if (!this.header) {
          return;
        }

        /* Matched on the prefix rather than a known list, so this keeps working
           when a merchant adds schemes in the theme editor. Same approach
           scheme-switcher.js takes. */
        Array.from(this.header.classList)
          .filter((name) => name.startsWith('color-'))
          .forEach((name) => this.header.classList.remove(name));

        if (schemeClass) {
          this.header.classList.add(schemeClass);
          this.header.setAttribute('data-scheme-override', '');
        } else {
          if (this.defaultSchemeClass) {
            this.header.classList.add(this.defaultSchemeClass);
          }
          this.header.removeAttribute('data-scheme-override');
        }
      }

      /**
       * Cross-fades to the logo drawn for a colour scheme. Pass the same scheme
       * CLASS applyHeaderScheme takes (e.g. 'color-scheme_2'), or null for the
       * default.
       *
       * The logos are stacked in a single grid cell and every one carries
       * opacity-0, so this only has to move the data-logo-active attribute —
       * the fade is entirely CSS, and the outgoing and incoming marks cross over
       * rather than one blinking out before the other appears.
       *
       * Falls back to the default whenever that scheme has no artwork, which is
       * the common case: a merchant only uploads variants for the schemes where
       * the default mark does not read.
       *
       * This governs the OPEN-PANEL state only. At rest the section's :has() rules
       * pick the logo from the section below, at a specificity that beats
       * data-logo-active — and they stop matching the moment applyHeaderScheme
       * sets data-scheme-override. That attribute is the switch between the two.
       */
      showLogo(schemeClass) {
        if (!this.defaultLogo) {
          return;
        }

        const target = schemeClass
          ? (this.schemeLogos.find((logo) => logo.dataset.headerLogoScheme === schemeClass) ?? null)
          : null;

        const active = target ?? this.defaultLogo;

        this.allLogos.forEach((logo) => {
          logo.toggleAttribute('data-logo-active', logo === active);
        });
      }

      /* ----------------------------------------------------------------- */
      /* Drawer                                                            */
      /* ----------------------------------------------------------------- */

      handleOpen() {
        if (this.dialog.open) {
          return;
        }

        /* The drawer covers the whole screen; leaving the search panel open
           behind it would strand the header on the search colour scheme. */
        this.closeSearch();

        if (this.closeTimer) {
          clearTimeout(this.closeTimer);
          this.closeTimer = null;
        }

        this.dialog.showModal();
        this.openButton.setAttribute('aria-expanded', 'true');

        /* Belt and braces with the panel's `autofocus`. showModal() focuses the
           first focusable element unless something carries autofocus — and the
           first one here is the logo link, which then shows a focus ring the
           visitor did not ask for, because programmatic focus cannot be
           attributed to the tap that opened the drawer.
           `autofocus` handles that declaratively, but browsers vary on whether
           they honour it on a non-form element, so this pins the end state
           everywhere. */
        this.panel.focus();

        /* Two frames, not one. The first gets the panel laid out at
           translateX(-100%); the second applies the end state so there is
           something to interpolate from. A single rAF is enough in most
           engines and not in all of them, and the failure mode is a drawer
           that snaps rather than slides — invisible in code review. */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.panel.dataset.open = '';
          });
        });
      }

      /**
       * Runs the slide-out, then actually closes the dialog. Closing it
       * immediately would remove it from the layout mid-transition and the
       * animation would never be seen.
       */
      handleClose() {
        if (!this.dialog.open) {
          return;
        }

        delete this.panel.dataset.open;

        const finish = () => {
          if (this.closeTimer) {
            clearTimeout(this.closeTimer);
            this.closeTimer = null;
          }
          this.panel.removeEventListener('transitionend', onEnd);

          if (this.dialog.open) {
            this.dialog.close();
          }
        };

        /* Guarded on propertyName: the panel also transitions nothing else
           today, but a future opacity or colour transition on the same element
           would otherwise close the drawer early. */
        const onEnd = (event) => {
          if (event.target === this.panel && event.propertyName === 'transform') {
            finish();
          }
        };

        this.panel.addEventListener('transitionend', onEnd);

        /* Covers both the reduced-motion case, where motion-safe: means there is
           no transition and transitionend never fires at all, and a transition
           interrupted by the tab being backgrounded. */
        this.closeTimer = setTimeout(finish, SiteHeader.CLOSE_FALLBACK_MS);
      }

      /** Esc. Cancelled so the close runs through the animation like any other. */
      handleCancel(event) {
        event.preventDefault();
        this.handleClose();
      }

      /**
       * Fires however the dialog was closed, including paths that bypass
       * handleClose entirely (form submission, a browser-initiated close). Keeps
       * the trigger's aria-expanded and the panel's transform state honest.
       */
      handleDialogClose() {
        this.openButton.setAttribute('aria-expanded', 'false');
        delete this.panel.dataset.open;
      }

      /**
       * The drawer is full width, so there is no visible backdrop to click —
       * but the dialog element still receives clicks that land outside the
       * panel's box, and treating those as a dismiss costs three lines.
       */
      handleBackdropClick(event) {
        if (event.target === this.dialog) {
          this.handleClose();
        }
      }

      /* ----------------------------------------------------------------- */
      /* Accordions                                                        */
      /* ----------------------------------------------------------------- */

      handleAccordion(event) {
        const toggle = event.currentTarget;
        const panel = this.querySelector(`#${CSS.escape(toggle.getAttribute('aria-controls'))}`);

        if (!panel) {
          return;
        }

        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        const next = !isOpen;

        toggle.setAttribute('aria-expanded', String(next));
        panel.hidden = !next;

        /* The `hidden` attribute, never a display utility. Preflight sets
           [hidden] to display:none !important, so it cannot lose a specificity
           race to a class on the same element. */
        const plus = toggle.querySelector('[data-accordion-icon-plus]');
        const minus = toggle.querySelector('[data-accordion-icon-minus]');

        if (plus) plus.hidden = next;
        if (minus) minus.hidden = !next;
      }

      /* ----------------------------------------------------------------- */

      /**
       * The drawer is hidden above 768px. A visitor who opens it on a phone and
       * rotates to landscape, or drags a desktop window wider, would otherwise
       * be left with an open modal they cannot see but which still traps focus
       * and holds the page scroll lock.
       */
      handleBreakpoint() {
        if (this.desktop.matches) {
          if (this.dialog?.open) {
            delete this.panel.dataset.open;
            this.dialog.close();
          }
          return;
        }

        /* Dropped below 768px. The nav that owns the mega menus is display:none
           down here, so an open panel would leave the header stuck on that
           menu's colour scheme and logo with no way to dismiss it. */
        this.closeMega();
      }
    },
  );
}
